import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, MessagesSquare } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLive } from "@/lib/useLive";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChatThread } from "@/components/ChatThread";

const ROLE = { business: "Maestro", customer: "Alumno" };
const when = (iso) => new Date(iso).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "America/Santiago" });

export function AdminChat({ userId, onSent }) {
  const [d, setD] = useState(null);
  const load = () => api.get(`/admin/messages/user/${userId}`).then((r) => { setD(r.data); window.dispatchEvent(new Event("vt-admin-msgs")); }).catch(() => setD({ messages: [] }));
  useEffect(() => { setD(null); load(); }, [userId]); // eslint-disable-line
  useLive(load, 5000);
  const send = async (text) => {
    try { const { data } = await api.post(`/admin/messages/user/${userId}`, { text }); setD((x) => ({ ...x, messages: [...(x?.messages || []), data] })); onSent?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); throw e; }
  };
  return <ChatThread messages={d ? d.messages : null} mine="admin" otherLabel={d?.user?.name || d?.user?.email || "Usuario"} onSend={send} testId="admin-chat"
    empty="Aún no hay mensajes con este usuario. Escribe el primero." />;
}

function ThreadRow({ t, active, onOpen }) {
  return (
    <button type="button" onClick={onOpen} className={`w-full text-left px-4 py-3 flex gap-3 items-start transition-colors ${active ? "bg-primary/10" : "hover:bg-muted/60"}`} data-testid={`admin-thread-${t.user_id}`}>
      <div className="min-w-0 flex-1">
        <p className="font-semibold truncate text-sm">{t.user_name || t.user_email}<span className="ml-1.5 text-[10px] font-medium text-muted-foreground">{ROLE[t.user_role] || ""}</span></p>
        <p className={`text-xs truncate ${t.unread_admin ? "text-foreground font-medium" : "text-muted-foreground"}`}>{t.last_sender === "admin" ? "Tú: " : ""}{t.last_preview}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-[10px] text-muted-foreground">{when(t.last_message_at)}</p>
        {t.unread_admin > 0 && <span className="mt-1 inline-grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-white text-[10px] font-bold" data-testid={`admin-thread-unread-${t.user_id}`}>{t.unread_admin}</span>}
      </div>
    </button>
  );
}

export function AdminMessages({ onCount }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/messages/threads").then((r) => { setRows(r.data.threads); onCount?.(r.data.unread); });
  useEffect(() => {
    load();
    window.addEventListener("vt-admin-msgs", load);
    return () => window.removeEventListener("vt-admin-msgs", load);
  }, []); // eslint-disable-line
  useLive(load, 10000);
  const pick = (t) => { setOpen(t); setRows((rs) => rs.map((r) => (r.user_id === t.user_id ? { ...r, unread_admin: 0 } : r))); setTimeout(load, 800); };
  return (
    <div className="pt-6 pb-20 lg:pb-0" data-testid="admin-messages">
      <Card className="overflow-hidden grid md:grid-cols-[320px_1fr] h-[calc(100dvh-22rem)] lg:h-[70vh] min-h-[340px] lg:min-h-[460px]">
        <div className={`border-r overflow-y-auto divide-y ${open ? "hidden md:block" : ""}`} data-testid="admin-threads">
          {rows?.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">Aún no hay conversaciones. Escríbele a un usuario desde la pestaña Usuarios.</p>}
          {rows?.map((t) => <ThreadRow key={t.user_id} t={t} active={open?.user_id === t.user_id} onOpen={() => pick(t)} />)}
        </div>
        <div className={`flex flex-col min-h-0 ${open ? "" : "hidden md:flex"}`}>
          {open ? (<>
            <div className="flex items-center gap-2 border-b px-3 py-2.5">
              <Button size="icon" variant="ghost" className="md:hidden h-8 w-8" onClick={() => setOpen(null)} aria-label="Volver" data-testid="admin-chat-back"><ArrowLeft className="w-4 h-4" /></Button>
              <div className="min-w-0"><p className="font-semibold text-sm truncate">{open.user_name || open.user_email}</p><p className="text-xs text-muted-foreground truncate">{open.user_email}</p></div>
            </div>
            <div className="flex-1 min-h-0"><AdminChat userId={open.user_id} onSent={load} /></div>
          </>) : (
            <div className="flex-1 grid place-items-center text-muted-foreground text-sm"><span className="flex flex-col items-center gap-2"><MessagesSquare className="w-8 h-8 text-primary/60" />Elige una conversación</span></div>
          )}
        </div>
      </Card>
    </div>
  );
}
